/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { Card, Descriptions, Space, Spin, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentContainer } from '@/components/ContentContainer';
import { PageHeader } from '@/components/PageHeader';
import { useM2MClientQuery } from '@/hooks/query/useM2MClientQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { DeleteM2MClientModal } from '@/pages/M2MClients/DeleteM2MClientModal';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';

const { Title } = Typography;

export function M2MClientDetailsPage() {
  const { t } = useMimicTranslation('m2mClients');
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [clientToDelete, setClientToDelete] = useState<{ id: string; name: string } | undefined>();

  const { data: m2mClient, isLoading } = useM2MClientQuery({ clientId });

  const handleBack = () => {
    navigate(-1);
  };

  const handleDelete = () => {
    if (m2mClient?.data) {
      setClientToDelete({ id: m2mClient.data.id, name: m2mClient.data.name });
    }
  };

  const handleDeleteSuccess = () => {
    navigate(-1);
  };

  if (!clientId) return null;

  if (isLoading) {
    return (
      <ContentContainer width="100%">
        <Container full style={{ width: '100%', padding: '48px', textAlign: 'center' }}>
          <Spin size="large" />
        </Container>
      </ContentContainer>
    );
  }

  if (!m2mClient?.data) {
    return (
      <ContentContainer width="100%">
        <Container full style={{ width: '100%', padding: '48px' }}>
          <Text>{t('clientNotFound')}</Text>
        </Container>
      </ContentContainer>
    );
  }

  const client = m2mClient.data;

  return (
    <ContentContainer width="100%">
      <Container gap="lg" style={{ padding: '24px', width: '100%' }}>
        <Button type="text" onClick={handleBack} icon={<ArrowLeftOutlined />} style={{ alignSelf: 'flex-start' }}>
          {t('backToList')}
        </Button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <PageHeader title={client.name} level={2} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button type="primary" icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('delete')}
            </Button>
          </div>
        </div>

        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Card>
            <Container gap="md">
              <Title level={4} style={{ margin: 0 }}>
                {t('detailsPage.basicInfo')}
              </Title>
              <Descriptions
                column={1}
                bordered
                size="small"
                contentStyle={{ padding: '8px 12px', fontSize: '13px' }}
                labelStyle={{ padding: '8px 12px' }}
              >
                <Descriptions.Item label={t('detailsPage.clientId')}>
                  <Text mono copyable>
                    {client.id}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('detailsPage.name')}>{client.name}</Descriptions.Item>
              </Descriptions>
            </Container>
          </Card>

          <Card>
            <Container gap="md">
              <Title level={4} style={{ margin: 0 }}>
                {t('detailsPage.allowedScopes')}
              </Title>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {client.allowedScopes.map((scope) => (
                  <Text
                    key={scope}
                    mono
                    style={{
                      background: '#f5f5f5',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '13px',
                    }}
                  >
                    {scope}
                  </Text>
                ))}
              </div>
            </Container>
          </Card>

          <Card>
            <Container gap="md">
              <Title level={4} style={{ margin: 0 }}>
                {t('detailsPage.configuration')}
              </Title>
              <Descriptions
                column={1}
                bordered
                size="small"
                contentStyle={{ padding: '8px 12px', fontSize: '13px' }}
                labelStyle={{ padding: '8px 12px' }}
              >
                <Descriptions.Item label={t('detailsPage.accessTokenTtl')}>
                  <Text>{client.accessTokenTtl}s</Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('detailsPage.supportedGrantTypes')}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {client.supportedGrantTypes.map((grantType) => (
                      <Text
                        key={grantType}
                        mono
                        style={{
                          background: '#f5f5f5',
                          padding: '2px 6px',
                          borderRadius: '3px',
                        }}
                      >
                        {grantType}
                      </Text>
                    ))}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Container>
          </Card>
        </Space>
      </Container>

      {clientToDelete && (
        <DeleteM2MClientModal
          clientId={clientToDelete.id}
          clientName={clientToDelete.name}
          onCancelDeletion={() => setClientToDelete(undefined)}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </ContentContainer>
  );
}
